import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, BookOpen, ArrowRight } from 'lucide-react';
import { getCategoryEnhancedDescription } from '../data/categoryDescriptions';
import { getAllPosts } from '../blog/blogPosts';
import type { BlogCategoryId } from '../blog/categories';

interface CategoryRelatedContentProps {
  categoryId: BlogCategoryId;
  locale: string;
}

/**
 * CategoryRelatedContent Component
 *
 * Displays related blog posts and knowledge articles for a category
 * Uses canonical URLs and locale-correct links
 * SSR-friendly
 */
export const CategoryRelatedContent: React.FC<CategoryRelatedContentProps> = ({
  categoryId,
  locale,
}) => {
  const enhanced = getCategoryEnhancedDescription(categoryId);

  if (!enhanced) {
    return null;
  }

  const allPosts = getAllPosts(locale);

  // Get related blog posts
  const relatedPosts = enhanced.relatedBlogSlugs
    .map((slug) => allPosts.find((post) => post.slug === slug))
    .filter(Boolean)
    .slice(0, 5);

  // Get related knowledge articles
  const relatedKnowledge = enhanced.relatedKnowledgeSlugs || [];

  // Translations
  const t = {
    relatedArticles: {
      en: 'Related Articles',
      de: 'Verwandte Artikel',
      'pt-BR': 'Artigos Relacionados',
      'ms-MY': 'Artikel Berkaitan',
      es: 'Artículos Relacionados',
      fr: 'Articles Connexes',
      ja: '関連記事',
      id: 'Artikel Terkait',
    },
    knowledgeBase: {
      en: 'Knowledge Base',
      de: 'Wissensdatenbank',
      'pt-BR': 'Base de Conhecimento',
      'ms-MY': 'Pangkalan Pengetahuan',
      es: 'Base de Conocimiento',
      fr: 'Base de Connaissances',
      ja: 'ナレッジベース',
      id: 'Basis Pengetahuan',
    },
    readMore: {
      en: 'Read more',
      de: 'Weiterlesen',
      'pt-BR': 'Leia mais',
      'ms-MY': 'Baca lagi',
      es: 'Leer más',
      fr: 'En savoir plus',
      ja: '続きを読む',
      id: 'Baca selengkapnya',
    },
    exploreKnowledge: {
      en: 'Explore',
      de: 'Erkunden',
      'pt-BR': 'Explorar',
      'ms-MY': 'Terokai',
      es: 'Explorar',
      fr: 'Découvrir',
      ja: '詳しく見る',
      id: 'Jelajahi',
    },
  };

  const localeKey = locale as keyof typeof t.relatedArticles;
  const getTranslation = (key: keyof typeof t) => t[key][localeKey] || t[key].en;

  // Build blog URL prefix
  const getLocalePrefix = (loc: string) => {
    const prefixMap: Record<string, string> = {
      'pt-BR': 'pt-br',
      'ms-MY': 'my',
      es: 'es',
      fr: 'fr',
      ja: 'ja',
      id: 'id',
    };
    return prefixMap[loc] || loc;
  };
  const localePrefix = getLocalePrefix(locale);
  const blogPrefix = locale === 'en' ? '/blog' : `/${localePrefix}/blog`;
  const knowledgePathSegmentMap: Record<string, string> = {
    de: 'wissen',
    'pt-BR': 'conhecimento',
    'ms-MY': 'pengetahuan',
    es: 'conocimiento',
    fr: 'connaissances',
    ja: 'knowledge',
    id: 'pengetahuan',
  };
  const knowledgePathSegment = knowledgePathSegmentMap[locale] || 'knowledge';
  const knowledgePrefix = locale === 'en' ? '/knowledge' : `/${localePrefix}/${knowledgePathSegment}`;

  return (
    <div className="mt-16 mb-12">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Related Blog Posts */}
        {relatedPosts.length > 0 && (
          <div className="bg-white dark:bg-charcoal-800 rounded-xl p-6 border border-neutral-200 dark:border-charcoal-700">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-accent-100 dark:bg-accent-900/30 flex items-center justify-center">
                <FileText className="w-5 h-5 text-accent-600 dark:text-accent-400" />
              </div>
              <h2 className="text-xl font-bold text-charcoal-900 dark:text-white">
                {getTranslation('relatedArticles')}
              </h2>
            </div>

            <div className="space-y-4">
              {relatedPosts.map((post) => {
                if (!post) return null;

                return (
                  <Link
                    key={post.slug}
                    to={`${blogPrefix}/${post.slug}`}
                    className="block group"
                  >
                    <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-neutral-50 dark:hover:bg-charcoal-700/50 transition-all">
                      <ArrowRight className="w-5 h-5 text-accent-600 dark:text-accent-400 flex-shrink-0 mt-0.5 group-hover:translate-x-1 transition-transform" />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-charcoal-900 dark:text-white group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors line-clamp-2 mb-1">
                          {post.title}
                        </h3>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400 line-clamp-2">
                          {post.description}
                        </p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Related Knowledge Articles */}
        {relatedKnowledge.length > 0 && (
          <div className="bg-gradient-to-br from-accent-50 to-accent-100/30 dark:from-accent-900/20 dark:to-accent-800/10 rounded-xl p-6 border border-accent-200 dark:border-accent-700/30">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-accent-600 dark:bg-accent-700 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-charcoal-900 dark:text-white">
                {getTranslation('knowledgeBase')}
              </h2>
            </div>

            <div className="space-y-3">
              {relatedKnowledge.map((slug) => {
                // Generate knowledge article titles based on slug
                const titles: Record<string, Record<string, string>> = {
                  'what-are-ai-watermarks': {
                    en: 'What Are AI Watermarks?',
                    de: 'Was sind KI-Wasserzeichen?',
                    'pt-BR': 'O Que São Marcas d\'Água de IA?',
                    'ms-MY': 'Apakah Watermark AI?',
                    es: '¿Qué son las marcas de agua IA?',
                    fr: 'Qu\'est-ce qu\'un filigrane IA ?',
                    ja: 'AI透かしとは？',
                    id: 'Apa Itu Watermark AI?',
                  },
                  'watermark-removal-vs-ai-detection': {
                    en: 'Watermark Removal vs AI Detection',
                    de: 'Wasserzeichen-Entfernung vs. KI-Erkennung',
                    'pt-BR': 'Remoção de Marca d\'Água vs Detecção de IA',
                    'ms-MY': 'Penyingkiran Watermark vs Pengesanan AI',
                    es: 'Eliminar marcas de agua vs detección IA',
                    fr: 'Suppression de filigrane vs détection IA',
                    ja: '透かし除去 vs AI検出',
                    id: 'Hapus Watermark vs Deteksi AI',
                  },
                  'token-distribution-ai-watermarking': {
                    en: 'Token Distribution in AI Watermarking',
                    de: 'Token-Verteilung bei KI-Wasserzeichen',
                    'pt-BR': 'Distribuição de Tokens em Marca d\'Água IA',
                    'ms-MY': 'Pengagihan Token dalam Watermark AI',
                    es: 'Distribución de tokens en marcas de agua IA',
                    fr: 'Distribution des tokens dans les filigranes IA',
                    ja: 'AI透かしにおけるトークン分布',
                    id: 'Distribusi Token pada Watermark AI',
                  },
                  'why-ai-detectors-fail': {
                    en: 'Why AI Detectors Fail',
                    de: 'Warum KI-Detektoren versagen',
                    'pt-BR': 'Por Que Detectores de IA Falham',
                    'ms-MY': 'Mengapa Pengesan AI Gagal',
                    es: '¿Por qué fallan los detectores de IA?',
                    fr: 'Pourquoi les détecteurs IA échouent',
                    ja: 'AI検出ツールが失敗する理由',
                    id: 'Mengapa Detektor AI Gagal?',
                  },
                  'why-short-texts-break-detection': {
                    en: 'Why Short Texts Break Detection',
                    de: 'Warum kurze Texte die Erkennung durchbrechen',
                    'pt-BR': 'Por Que Textos Curtos Quebram a Detecção',
                    'ms-MY': 'Mengapa Teks Pendek Pecahkan Pengesanan',
                    es: '¿Por qué los textos cortos evaden la detección?',
                    fr: 'Pourquoi les textes courts déjouent la détection',
                    ja: '短文がAI検出を回避する理由',
                    id: 'Mengapa Teks Pendek Lolos Deteksi?',
                  },
                };

                const title = titles[slug]?.[localeKey] || titles[slug]?.en || slug;

                return (
                  <Link
                    key={slug}
                    to={`${knowledgePrefix}/${slug}`}
                    className="block group"
                  >
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-charcoal-800/50 hover:bg-white dark:hover:bg-charcoal-700 transition-all border border-accent-200/50 dark:border-accent-700/20">
                      <div className="flex-1">
                        <span className="font-semibold text-charcoal-900 dark:text-white group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors">
                          {title}
                        </span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-accent-600 dark:text-accent-400 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryRelatedContent;
